"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import { ButtonLink } from "@/components/ui/button-link";

interface Step {
  title: string;
  description: string;
  icon: string;
}

interface HowToSectionProps {
  title: string;
  steps: Step[];
  note: string;
  ctaText: string;
  ctaHref?: string;
}

const HowToSection = ({ title, steps, note, ctaText, ctaHref }: HowToSectionProps) => {
  return (
    <section className="py-24 px-6 bg-white">
      <div className="container max-w-6xl mx-auto flex flex-col items-center gap-16">
        {/* Header */}
        <BlurFade delay={0.1}>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground text-center">
            {title}
          </h2>
        </BlurFade>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
          {steps.map((step, i) => (
            <BlurFade key={i} delay={0.2 + i * 0.1}>
              <div className="flex flex-col items-center text-center gap-8 bg-muted p-10 rounded-[2.5rem] border border-transparent hover:border-zinc-100 transition-all h-full">
                {/* Grey Placeholder Graphic */}
                <div className="w-full aspect-[4/3] bg-zinc-200/50 rounded-2xl flex items-center justify-center text-4xl grayscale opacity-50 relative overflow-hidden">
                  <div className="absolute inset-0 border-2 border-dashed border-zinc-300 rounded-2xl m-4" />
                  <span className="relative z-10">{step.icon}</span>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-foreground">
                    {step.title}
                  </h3>
                  <p className="text-sm text-foreground/60 font-medium leading-relaxed max-w-[280px] mx-auto">
                    {step.description}
                  </p>
                </div>
              </div>
            </BlurFade>
          ))}
        </div>

        {/* Bottom Note & Action */}
        <div className="flex flex-col items-center gap-8">
          <BlurFade delay={0.6}>
            <p className="text-[11px] font-medium text-foreground/40 text-center max-w-2xl">
              {note}
            </p>
          </BlurFade>

          <BlurFade delay={0.7}>
            <ButtonLink
              href={ctaHref}
              variant="secondary-dark"
              size="sm"
            >
              {ctaText}
            </ButtonLink>
          </BlurFade>
        </div>

      </div>
    </section>
  );
};

export default HowToSection;





